import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { ScrapeJob } from '../types';
import { ScrapeContext } from './ScrapeContext';
import { saveScrapeJob, fetchScrapeJobs, updateScrapeJob } from '../services/db';
import { useAuth } from '../hooks/useAuth';

export const ScrapeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [jobs, setJobs] = useState<ScrapeJob[]>([]);
    const [loading, setLoading] = useState(true);
    const { user, isAuthenticated } = useAuth();

    const isFetchingRef = useRef(false);

    const fetchJobs = useCallback(async (silent = false) => {
        const token = localStorage.getItem('token');
        if (!isAuthenticated || !token) {
            setJobs([]);
            setLoading(false);
            return;
        }

        // Prevent parallel overlapping fetches using Ref (stable identity)
        if (isFetchingRef.current) return;

        try {
            if (!silent) setLoading(true);
            isFetchingRef.current = true;
            const fetchedJobs = await fetchScrapeJobs();
            setJobs(fetchedJobs);
        } catch (error) {
            console.error('Error fetching jobs:', error);
            // Only clear jobs if it was a catastrophic failure on initial load
            if (!silent) setJobs([]);
        } finally {
            if (!silent) setLoading(false);
            isFetchingRef.current = false;
        }
    }, [isAuthenticated]);

    const addJob = async (job: ScrapeJob): Promise<ScrapeJob> => {
        try {
            const savedJob = await saveScrapeJob(job);
            setJobs(prev => [savedJob, ...prev]);
            return savedJob;
        } catch (error) {
            console.error('Error saving job:', error);
            throw error;
        }
    };

    const updateJob = async (id: string, updates: Partial<ScrapeJob>) => {
        try {
            const updatedJob = await updateScrapeJob(id, updates);
            setJobs(prev => prev.map(job => job.id === id ? updatedJob : job));
        } catch (error) {
            console.error('Error updating job:', error);
            throw error;
        }
    };

    useEffect(() => {
        fetchJobs();
    }, [user?.id, isAuthenticated, fetchJobs]);

    return (
        <ScrapeContext.Provider value={{ jobs, addJob, updateJob, fetchJobs, loading }}>
            {children}
        </ScrapeContext.Provider>
    );
};